import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2,
  ArrowLeft,
  Printer,
  Download,
  ExternalLink,
  User,
  GraduationCap,
  Hash,
  BookOpen,
  FileText,
  Save,
} from "lucide-react";

import {
  getReportCardPrintPayload,
  updateReportCardRemarks,
  downloadReportCardPdf,
  getReportCardHtmlUrl,
} from "../../api/reportcard/reportCardApi";
import {
  getReportCardPrintPayloadPortal,
  updateReportCardRemarksPortal,
  downloadReportCardPdfPortal,
  getReportCardHtmlUrlPortal,
} from "../../api/reportcard/reportCardPortalApi";
import {
  getMyReportCardDetail,
  downloadMyReportCardPdf,
  getMyReportCardHtmlUrl,
} from "../../api/reportcard/reportCardStudentApi";
import { useStaffAuth } from "../../context/StaffAuthContext";
import ResultStatusBadge from "../../components/reportcard/ResultStatusBadge";
import { inputCls, labelCls } from "../../utils/reportcard/reportCardHelpers";
import { useToast } from "../../context/ToastContext";

const MatrixTable = ({ title, columns, rows, color, secondaryColor }) => {
  const cols = columns || [];
  const list = rows || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div
          className="h-3 w-1 rounded-full"
          style={{ backgroundColor: color }}
        />
        <h3 className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-neutral-800 dark:text-neutral-100">
          {title}
        </h3>
      </div>

      {!cols.length ? (
        <p className="text-xs text-neutral-400 dark:text-neutral-500 italic px-1">
          No subjects recorded for this section.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-xs">
          <table className="w-full text-left text-xs sm:text-sm border-collapse">
            <thead>
              <tr
                style={{ background: color }}
                className="text-white divide-x divide-white/10"
              >
                <th className="px-3 py-3 font-semibold tracking-wide">Subject</th>
                {cols.map((c) => (
                  <th
                    key={c.key}
                    className="px-3 py-3 font-semibold text-center tracking-wide min-w-[80px]"
                  >
                    <div>{c.label}</div>
                    <div className="text-[10px] font-normal opacity-85 mt-0.5">
                      FM: {c.fullMarks}
                    </div>
                  </th>
                ))}
                <th className="px-3 py-3 font-semibold text-center tracking-wide">
                  Total
                </th>
                <th className="px-3 py-3 font-semibold text-center tracking-wide">
                  Grade
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-neutral-800">
              {list.map((r, i) => (
                <tr
                  key={i}
                  className={`hover:bg-[#F5F5FB] transition-colors divide-x divide-slate-100 dark:divide-neutral-800 text-neutral-700 dark:text-neutral-200 ${
                    i % 2 === 1 ? "bg-neutral-50 dark:bg-neutral-900/40" : ""
                  }`}
                >
                  <td className="px-3 py-3 font-medium text-neutral-900 dark:text-neutral-100">
                    {r.subjectName}
                  </td>
                  {cols.map((c) => {
                    const cell = (r.cells || []).find(
                      (x) => x.columnKey === c.key
                    );
                    let display = "—";
                    let isAbsent = false;
                    if (cell?.isAbsent) {
                      display = "Ab";
                      isAbsent = true;
                    } else if (
                      cell &&
                      !cell.missing &&
                      cell.marksObtained != null
                    )
                      display = cell.marksObtained;
                    return (
                      <td
                        key={c.key}
                        className={`px-3 py-3 text-center font-medium ${
                          isAbsent ? "text-danger-600 dark:text-danger-500 font-bold" : ""
                        }`}
                      >
                        {display}
                      </td>
                    );
                  })}
                  <td className="px-3 py-3 text-center font-semibold text-neutral-900 dark:text-neutral-100">
                    {r.totalMarksObtained ?? "—"}/{r.totalMaxMarks || 0}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span
                      className="inline-block min-w-[26px] px-2 py-0.5 rounded font-bold"
                      style={{
                        background: secondaryColor || "#EEF2FF",
                        color,
                      }}
                    >
                      {r.grade || "—"}
                    </span>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr>
                  <td
                    colSpan={cols.length + 3}
                    className="px-3 py-8 text-center text-neutral-400 dark:text-neutral-500 text-xs"
                  >
                    No assessment rows found.
                  </td>
                </tr>
              )}
              {list.length > 0 && (
                <tr className="bg-slate-100 dark:bg-neutral-900 font-bold text-neutral-900 dark:text-neutral-100 divide-x divide-slate-200 dark:divide-neutral-800">
                  <td className="px-3 py-2.5">Total</td>
                  {cols.map((c) => {
                    let obtained = 0;
                    let max = 0;
                    let any = false;
                    list.forEach((r) => {
                      const cell = (r.cells || []).find(
                        (x) => x.columnKey === c.key
                      );
                      if (
                        cell &&
                        !cell.missing &&
                        !cell.isAbsent &&
                        cell.marksObtained != null
                      ) {
                        obtained += Number(cell.marksObtained);
                        max +=
                          Number(cell.maxMarks) || Number(c.fullMarks) || 0;
                        any = true;
                      }
                    });
                    return (
                      <td key={c.key} className="px-3 py-2.5 text-center">
                        {any
                          ? `${Math.round(obtained * 100) / 100}/${max}`
                          : "—"}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2.5 text-center" colSpan={2}>
                    {list.reduce(
                      (a, r) => a + (Number(r.totalMarksObtained) || 0),
                      0
                    )}
                    /
                    {list.reduce(
                      (a, r) => a + (Number(r.totalMaxMarks) || 0),
                      0
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const GradingLegend = ({ gradeScheme, color }) => {
  const bands = gradeScheme?.gradeBands || [];
  if (!bands.length) return null;
  const sorted = [...bands].sort(
    (a, b) => Number(b.minPercentage) - Number(a.minPercentage)
  );
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div
          className="h-3 w-1 rounded-full"
          style={{ backgroundColor: color }}
        />
        <h3 className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-neutral-800 dark:text-neutral-100">
          Grading Scale
        </h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {sorted.map((b) => (
          <div
            key={b.grade}
            className="px-3 py-1.5 rounded-lg text-center min-w-[64px]"
            style={{ background: color, color: "#fff" }}
          >
            <div className="text-xs font-bold">{b.grade}</div>
            <div className="text-[10px] opacity-85">
              {b.minPercentage}–{b.maxPercentage}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ReportCardView = ({ isPortal = false, isStudent = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();
  const canManage =
    !isStudent &&
    (isPortal ? staffAuth?.hasPermission?.("REPORT_CARD_FULL") : true);

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teacherRemarks, setTeacherRemarks] = useState("");
  const [principalRemarks, setPrincipalRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const toast = useToast();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      let res;
      if (isStudent) {
        res = await getMyReportCardDetail(id);
      } else if (isPortal) {
        res = await getReportCardPrintPayloadPortal(id);
      } else {
        res = await getReportCardPrintPayload(id);
      }
      const nextPayload = res.payload || res;
      setPayload(nextPayload);
      setTeacherRemarks(nextPayload?.summary?.teacherRemarks || "");
      setPrincipalRemarks(nextPayload?.summary?.principalRemarks || "");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load card");
    } finally {
      setLoading(false);
    }
  }, [id, isPortal, isStudent]);

  useEffect(() => {
    load();
  }, [load]);

  const saveRemarks = async () => {
    if (isStudent) return;
    try {
      setSaving(true);
      if (isPortal)
        await updateReportCardRemarksPortal(id, {
          teacherRemarks,
          principalRemarks,
        });
      else
        await updateReportCardRemarks(id, {
          teacherRemarks,
          principalRemarks,
        });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handlePdfDownload = async () => {
    try {
      setPdfBusy(true);
      const blob = isStudent
        ? await downloadMyReportCardPdf(id)
        : isPortal
        ? await downloadReportCardPdfPortal(id)
        : await downloadReportCardPdf(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report-card-${payload?.student?.name || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let message = "PDF download failed — try HTML view + browser Print";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch {
          /* keep generic */
        }
      } else if (data?.message) {
        message = data.message;
      }
      toast.error(message);
    } finally {
      setPdfBusy(false);
    }
  };

  const openHtml = () => {
    const url = isStudent
      ? getMyReportCardHtmlUrl(id)
      : isPortal
      ? getReportCardHtmlUrlPortal(id)
      : getReportCardHtmlUrl(id);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const goBack = () => {
    if (isStudent) navigate("/student-panel/report-cards");
    else navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F5F5FB] text-neutral-500 dark:text-neutral-400 gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-[#4B3FAE]" />
        <p className="text-xs font-medium tracking-wide text-[#322687]">
          Loading report card...
        </p>
      </div>
    );
  }

  const color = payload?.meta?.primaryColor || "#4B3FAE";
  const majorCols = payload?.majorColumns || [];
  const minorCols = payload?.minorColumns || [];

  return (
    <div className="min-h-screen bg-[#F5F5FB] py-6 sm:py-8 px-3 sm:px-6 print:p-0 print:bg-white">
      <div className="max-w-5xl mx-auto space-y-6">
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:hidden">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:text-neutral-100 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800 px-3.5 py-2 rounded-xl shadow-xs transition-colors self-start sm:self-auto"
          >
            <ArrowLeft size={16} /> Back
          </button>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={openHtml}
              className="inline-flex items-center gap-2 border border-neutral-200 dark:border-neutral-800 text-xs sm:text-sm font-medium px-3.5 py-2 rounded-xl bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-200 shadow-xs transition-colors"
            >
              <ExternalLink size={16} />
              <span>HTML View</span>
            </button>
            <button
              type="button"
              disabled={pdfBusy}
              onClick={handlePdfDownload}
              className="inline-flex items-center gap-2 border border-neutral-200 dark:border-neutral-800 text-xs sm:text-sm font-medium px-3.5 py-2 rounded-xl bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-200 shadow-xs transition-colors disabled:opacity-50"
            >
              {pdfBusy ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Download size={16} />
              )}
              <span>{pdfBusy ? "Preparing…" : "Download PDF"}</span>
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 bg-[#4B3FAE] hover:bg-[#322687] text-white font-medium text-xs sm:text-sm px-4 py-2 rounded-xl shadow-xs transition-colors"
            >
              <Printer size={16} />
              <span>Print</span>
            </button>
          </div>
        </header>

        <article className="bg-white dark:bg-neutral-900 rounded-2xl shadow-xs border border-neutral-200 dark:border-neutral-800/80 overflow-hidden print:shadow-none print:border-0 print:rounded-none">
          <div
            className="px-6 sm:px-10 py-8 text-white relative"
            style={{ background: color }}
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3.5">
                {payload?.meta?.logoUrl && (
                  <img
                    src={payload.meta.logoUrl}
                    alt=""
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg object-contain bg-white/95 p-1 shrink-0"
                  />
                )}
                <div>
                  <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-white/80">
                    {payload?.meta?.headerSubtitle || "Report Card"}
                  </p>
                  <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">
                    {payload?.meta?.schoolName || "School"}
                  </h1>
                  {payload?.meta?.address && (
                    <p className="text-[11px] sm:text-xs text-white/80 mt-0.5">
                      {payload.meta.address}
                    </p>
                  )}
                </div>
              </div>
              <div className="md:text-right bg-white/10 backdrop-blur-xs px-4 py-2 rounded-xl border border-white/15 self-start md:self-auto">
                <p className="text-xs font-semibold text-white/90">
                  {payload?.examTitle}
                </p>
                <p className="text-[11px] text-white/75 mt-0.5">
                  Academic Year: {payload?.student?.academicYear}
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 sm:px-10 py-6 bg-[#F5F5FB]/60 border-b border-neutral-200 dark:border-neutral-800">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-white dark:bg-neutral-900 border border-[#E9E8F7] text-[#4B3FAE] shadow-2xs">
                  <User size={16} />
                </div>
                <div>
                  <span className="text-[10px] uppercase font-semibold text-neutral-400 dark:text-neutral-500 tracking-wider block">
                    Student
                  </span>
                  <span className="font-semibold text-neutral-900 dark:text-neutral-100 text-xs sm:text-sm">
                    {payload?.student?.name}
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-white dark:bg-neutral-900 border border-[#E9E8F7] text-[#4B3FAE] shadow-2xs">
                  <GraduationCap size={16} />
                </div>
                <div>
                  <span className="text-[10px] uppercase font-semibold text-neutral-400 dark:text-neutral-500 tracking-wider block">
                    Class & Sec
                  </span>
                  <span className="font-semibold text-neutral-800 dark:text-neutral-100 text-xs sm:text-sm">
                    {payload?.student?.className} ·{" "}
                    {payload?.student?.sectionName}
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-white dark:bg-neutral-900 border border-[#E9E8F7] text-[#4B3FAE] shadow-2xs">
                  <FileText size={16} />
                </div>
                <div>
                  <span className="text-[10px] uppercase font-semibold text-neutral-400 dark:text-neutral-500 tracking-wider block">
                    Admission No
                  </span>
                  <span className="font-medium text-neutral-700 dark:text-neutral-200 text-xs sm:text-sm">
                    {payload?.student?.admissionNo || "—"}
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-white dark:bg-neutral-900 border border-[#E9E8F7] text-[#4B3FAE] shadow-2xs">
                  <Hash size={16} />
                </div>
                <div>
                  <span className="text-[10px] uppercase font-semibold text-neutral-400 dark:text-neutral-500 tracking-wider block">
                    Roll No
                  </span>
                  <span className="font-medium text-neutral-700 dark:text-neutral-200 text-xs sm:text-sm">
                    {payload?.student?.rollNumber ?? "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 sm:px-10 py-6 space-y-6">
            <MatrixTable
              title="Scholastic Areas"
              columns={majorCols}
              rows={payload?.majorRows}
              color={color}
              secondaryColor={payload?.meta?.secondaryColor}
            />
            <MatrixTable
              title="Co-Scholastic Areas"
              columns={minorCols}
              rows={payload?.minorRows}
              color={color}
              secondaryColor={payload?.meta?.secondaryColor}
            />

            {!majorCols.length && !minorCols.length && (
              <div className="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-800">
                <table className="w-full text-left text-xs sm:text-sm">
                  <thead>
                    <tr className="bg-neutral-50 dark:bg-neutral-950 text-neutral-600 dark:text-neutral-300 border-b border-neutral-200 dark:border-neutral-800 font-semibold">
                      <th className="py-3 px-4">Subject</th>
                      <th className="py-3 px-4 text-center">Max</th>
                      <th className="py-3 px-4 text-center">Obtained</th>
                      <th className="py-3 px-4 text-center">%</th>
                      <th className="py-3 px-4 text-center">Grade</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-neutral-800">
                    {(payload?.subjects || []).map((s, i) => (
                      <tr key={i} className="hover:bg-neutral-50/50 dark:bg-neutral-950">
                        <td className="py-3 px-4 font-medium text-neutral-800 dark:text-neutral-100">
                          {s.name}
                        </td>
                        <td className="py-3 px-4 text-center text-neutral-600 dark:text-neutral-300">
                          {s.maxMarks}
                        </td>
                        <td className="py-3 px-4 text-center font-medium text-neutral-900 dark:text-neutral-100">
                          {s.marksObtained}
                        </td>
                        <td className="py-3 px-4 text-center text-neutral-600 dark:text-neutral-300">
                          {s.percentage ?? "—"}
                        </td>
                        <td className="py-3 px-4 text-center font-bold text-neutral-900 dark:text-neutral-100">
                          {s.grade}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <GradingLegend
              gradeScheme={payload?.gradeScheme}
              color={color}
            />
          </div>

          <div className="px-6 sm:px-10 py-6 bg-[#F5F5FB] border-t border-neutral-200 dark:border-neutral-800/80 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-white dark:bg-neutral-900 p-4 rounded-xl border border-neutral-200 dark:border-neutral-800/60 shadow-2xs">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
                  Total Marks
                </p>
                <p className="text-sm sm:text-base font-bold text-neutral-900 dark:text-neutral-100 mt-0.5">
                  {payload?.summary?.totalMarksObtained} /{" "}
                  {payload?.summary?.totalMaxMarks}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
                  Overall Score
                </p>
                <p className="text-sm sm:text-base font-bold text-neutral-900 dark:text-neutral-100 mt-0.5">
                  {payload?.summary?.overallPercentage}%
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
                  Grade & Result
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-sm sm:text-base font-bold text-neutral-900 dark:text-neutral-100">
                    {payload?.summary?.overallGrade}
                  </span>
                  <ResultStatusBadge status={payload?.summary?.resultStatus} />
                </div>
              </div>
              {payload?.meta?.showRank && payload?.summary?.rankInSection && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
                    Class Rank
                  </p>
                  <p className="text-sm sm:text-base font-bold text-neutral-900 dark:text-neutral-100 mt-0.5">
                    #{payload.summary.rankInSection}
                  </p>
                </div>
              )}
            </div>
            {payload?.summary?.promotionMessage && (
              <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-300 font-medium px-1">
                {payload.summary.promotionMessage}
              </p>
            )}
          </div>

          <div className="px-6 sm:px-10 py-8 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            <div className="border-t border-slate-300 dark:border-neutral-700 pt-2 text-xs text-neutral-500 dark:text-neutral-400 font-medium">
              Class Teacher
            </div>
            <div className="border-t border-slate-300 dark:border-neutral-700 pt-2 text-xs text-neutral-500 dark:text-neutral-400 font-medium">
              Principal
            </div>
            <div className="border-t border-slate-300 dark:border-neutral-700 pt-2 text-xs text-neutral-500 dark:text-neutral-400 font-medium">
              Parent / Guardian
            </div>
          </div>
        </article>

        {canManage && (
          <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-6 shadow-xs space-y-4 print:hidden">
            <div className="flex items-center gap-2 pb-2 border-b border-neutral-200 dark:border-neutral-800">
              <BookOpen size={18} className="text-[#4B3FAE]" />
              <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                Manage Remarks
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className={labelCls}>Teacher Remarks</label>
                <textarea
                  className={inputCls}
                  rows={3}
                  value={teacherRemarks}
                  onChange={(e) => setTeacherRemarks(e.target.value)}
                  placeholder="Add comments regarding performance..."
                />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Principal Remarks</label>
                <textarea
                  className={inputCls}
                  rows={3}
                  value={principalRemarks}
                  onChange={(e) => setPrincipalRemarks(e.target.value)}
                  placeholder="Add administrative comments..."
                />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                disabled={saving}
                onClick={saveRemarks}
                className="inline-flex items-center gap-2 bg-[#4B3FAE] hover:bg-[#322687] text-white text-xs sm:text-sm font-medium px-5 py-2.5 rounded-xl shadow-xs transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                <span>{saving ? "Saving..." : "Save Remarks"}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportCardView;